import type { PathButtonProps } from "shared/react";
import type { ChallengeData, CommentData, ListData, MessageData, NoteData, PostData, ProfileData, RewardData } from "shared/schema";
import type { DataKey, Database, Entity, ImmutableArray, Item, ItemStore } from "shelving";
import { limitString, splitEntity } from "shelving";
import { Icon, MediaIcon, MediaImage, PathButton, When } from "shared/react";
import { getGroupTitle, getMarkupExcerpt, getProfileTitle } from "shared/schema";
import { getMediaImageStyle } from "shared/util/style";
import { RewardIcon } from "../reward/RewardIcon.js";
import { HashTagButton } from "../tag/HashTagButton.js";
import { useItem } from "../util/db.js";
import { useRewardStyle } from "../util/reward.js";
import { ProfileName, getNopeReason } from "./EntityName.js";

const EXCERPT_LENGTH = 25;

function getNopeEntityButton<T extends Database, K extends DataKey<T>>(store: ItemStore<T, K>, props: PathButtonProps): React.ReactElement | undefined {
	const reason = getNopeReason(store);
	if (reason) {
		const { collection, id } = store;
		return (
			<PathButton path={`/${collection}/${id}`} {...props}>
				<figure className="photo" /> {reason}
			</PathButton>
		);
	}
}

/** Display any entity from an entity tag, e.g. `challenge:3ms9si...` */
export function EntityButton({ entity, ...props }: { entity: Entity } & PathButtonProps): React.ReactElement {
	const [type, id] = splitEntity(entity);
	return type === "profile" ? (
		<ProfileButton uid={id} {...props} />
	) : type === "group" ? (
		<GroupButton gid={id} {...props} />
	) : type === "challenge" ? (
		<ChallengeButton id={id} {...props} />
	) : type === "reward" ? (
		<RewardButton id={id} {...props} />
	) : type === "note" ? (
		<NoteButton id={id} {...props} />
	) : type === "list" ? (
		<ListButton id={id} {...props} />
	) : type === "tag" ? (
		<HashTagButton slug={id} {...props} />
	) : type === "post" ? (
		<PostButton id={id} {...props} />
	) : type === "comment" ? (
		<CommentButton id={id} {...props} />
	) : type === "message" ? (
		<MessageButton id={id} {...props} />
	) : (
		<button type="button" disabled>
			{entity}
		</button> //  Never happens.
	);
}

/** Output a list of entity buttons. */
export function EntityButtons({ entities, mods }: { entities: ImmutableArray<Entity>; mods?: string | undefined }): React.ReactElement | null {
	return entities.length ? (
		<nav className={mods}>
			{entities.map(entity => (
				<EntityButton key={entity} entity={entity} />
			))}
		</nav>
	) : null;
}

/** A button styled with the background of profile's background, group's cover image, or reward's colour. */
export function EntityBackgroundButton({ entity, ...props }: { entity: Entity } & PathButtonProps): React.ReactElement {
	const [type, id] = splitEntity(entity);
	if (type === "profile") return <ProfileBackgroundButton uid={id} {...props} />;
	else if (type === "group") return <GroupBackgroundButton gid={id} {...props} />;
	else if (type === "reward") return <RewardBackgroundButton id={id} {...props} />;
	else return <PathButton {...props} />;
}

export function ProfileBackgroundButton({ uid, children, ...props }: { uid: string } & PathButtonProps): React.ReactElement {
	const store = useItem("profile", uid);
	if (store.loading || !store.exists) return <PathButton {...props} />;
	const item = store.data;
	const { photo, background } = item;
	return (
		<RewardBackgroundButton id={background} {...props}>
			<figure className="photo" title={getProfileTitle(item)}>
				{photo ? <MediaImage path={photo} /> : <Icon icon="chat" />}
			</figure>
			{children}
		</RewardBackgroundButton>
	);
}

export function GroupBackgroundButton({ gid, children, ...props }: { gid: string } & PathButtonProps): React.ReactElement {
	const store = useItem("group", gid);
	if (store.loading || !store.exists) return <PathButton {...props}>{children}</PathButton>;
	const item = store.data;
	const { photo, cover, hue } = item;
	return (
		<PathButton style={getMediaImageStyle(cover, hue)} {...props}>
			<figure className="photo" title={getGroupTitle(item)}>
				{photo ? <MediaImage path={photo} /> : <Icon icon="chat" />}
			</figure>
			{children}
		</PathButton>
	);
}

export function RewardBackgroundButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("reward", id);
	if (store.loading || !store.exists) return <PathButton {...props} />;
	const { type, image, hue } = store.data;
	return <PathButton style={getMediaImageStyle(type === "background" ? image : null, hue)} {...props} />;
}

export function ListButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("list", id);
	return getNopeEntityButton(store, props) || <RawListButton item={store.data} {...props} />;
}

export function RawListButton({ item, path = `/list/${item.id}`, ...props }: { item: Item<ListData> } & PathButtonProps): React.ReactElement {
	const { creator, name } = item;
	return (
		<EntityBackgroundButton entity={creator} path={path} {...props}>
			<Icon icon="list" /> <span>{name}</span>
		</EntityBackgroundButton>
	);
}

export function NoteButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("note", id);
	return getNopeEntityButton(store, props) || <RawNoteButton item={store.data} {...props} />;
}

export function RawNoteButton({ item, path = `/note/${item.id}`, ...props }: { item: Item<NoteData> } & PathButtonProps): React.ReactElement {
	const { creator, name } = item;
	return (
		<EntityBackgroundButton entity={creator} path={path} {...props}>
			<Icon icon="note" /> <span>{name}</span>
		</EntityBackgroundButton>
	);
}

export function GroupButton({ gid, path, ...props }: { gid: string } & PathButtonProps): React.ReactElement {
	const { photo, cover, hue, name, username } = useItem("group", gid).data;
	return (
		<PathButton path={path ?? username ? `/${username}` : `/group/${gid}`} style={getMediaImageStyle(cover, hue)} {...props}>
			<figure className="photo">{photo ? <MediaImage path={photo} /> : <Icon icon="chat" />}</figure>
			<span>{name}</span>
		</PathButton>
	);
}

export function ChallengeButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("challenge", id);
	return getNopeEntityButton(store, props) || <RawChallengeButton item={store.data} {...props} />;
}

export function RawChallengeButton({ item, path = `/challenge/${item.id}`, ...props }: { item: Item<ChallengeData> } & PathButtonProps): React.ReactElement {
	const { badge, name, background } = item;
	return (
		<RewardBackgroundButton id={background} path={path} {...props}>
			<figure className="photo pad-hairline">
				<RewardIcon id={badge} type="badge" />
			</figure>
			<span>{name}</span>
		</RewardBackgroundButton>
	);
}

export function RewardButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("reward", id);
	return getNopeEntityButton(store, props) || <RawRewardButton item={store.data} {...props} />;
}

export function RawRewardButton({ item, path = `/reward/${item.id}`, ...props }: { item: Item<RewardData> } & PathButtonProps): React.ReactElement {
	const { name, image, hue, type } = item;
	return type === "background" ? (
		<PathButton path={path} style={getMediaImageStyle(image, hue)} {...props}>
			<figure className="photo" /> <span>{name}</span>
		</PathButton>
	) : (
		<PathButton path={path} style={getMediaImageStyle(null, hue)} {...props}>
			<figure className="photo pad-hairline">
				<MediaIcon path={image} />
			</figure>
			<span>{name}</span>
		</PathButton>
	);
}

export function ProfileButton({ uid, ...props }: { uid: string } & PathButtonProps): React.ReactElement {
	const store = useItem("profile", uid);
	return getNopeEntityButton(store, props) || <RawProfileButton item={store.data} {...props} />;
}

export function RawProfileButton({ item, path, ...props }: { item: Item<ProfileData> } & PathButtonProps): React.ReactElement {
	const { id: uid, photo, background, username } = item;
	return (
		<PathButton path={path ?? username ? `/${username}` : `/profile/${uid}`} style={useRewardStyle(background)} {...props}>
			<figure className="photo">{photo ? <MediaImage path={photo} /> : <Icon icon="profile" />}</figure>
			<span>{username}</span>
		</PathButton>
	);
}

export function PostButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("post", id);
	return getNopeEntityButton(store, props) || <RawPostButton item={store.data} {...props} />;
}

export function RawPostButton({ item, path = `/post/${item.id}`, ...props }: { item: Item<PostData> } & PathButtonProps): React.ReactElement {
	const { creator: uid, date, content } = item;
	return (
		<ProfileBackgroundButton uid={uid} path={path} {...props}>
			<span>
				<When target={date} />: <span>{`"${limitString(content, length)}"`}</span>
			</span>
		</ProfileBackgroundButton>
	);
}

export function CommentButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("comment", id);
	return getNopeEntityButton(store, props) || <RawCommentButton item={store.data} {...props} />;
}

export function RawCommentButton({ item, path = `/comment/${item.id}`, ...props }: { item: Item<CommentData> } & PathButtonProps): React.ReactElement {
	const { creator: uid, date, content } = item;
	return (
		<ProfileBackgroundButton uid={uid} path={path} {...props}>
			<span>
				<When target={date} />: <span className="weight-normal">{`"${limitString(content, length)}"`}</span>
			</span>
		</ProfileBackgroundButton>
	);
}

export function MessageButton({ id, ...props }: { id: string } & PathButtonProps): React.ReactElement {
	const store = useItem("message", id);
	return getNopeEntityButton(store, props) || <RawMessageButton item={store.data} {...props} />;
}

export function RawMessageButton({ item, path = `/message/${item.id}`, ...props }: { item: Item<MessageData> } & PathButtonProps): React.ReactElement {
	const { gid, creator: uid, date, content } = item;
	return (
		<GroupBackgroundButton gid={gid} path={path} {...props}>
			<span>
				<When target={date} />: <ProfileName uid={uid} /> <span className="weight-normal">{`"${getMarkupExcerpt(content, EXCERPT_LENGTH)}"`}</span>
			</span>
		</GroupBackgroundButton>
	);
}
